import { z } from 'zod';
import { t } from 'i18next';
import { isValidNum } from '@lili/lili-frontend-common';
import { ApartmentSchema } from '@Components/inputs/ApartmentInput';
import { emailSchema } from '@Components/inputs/EmailInput';
import { OnboardingFormFields } from '@Types/index.ts';
import { ssnSchema } from '@Schema/ssnSchema';
import { Line1Schema } from '@Components/inputs/Line1';
import { citySchema } from '@Components/inputs/CityInput';
import { getZipSchemeByCountry, zipSchema } from '@Components/inputs/ZipAddress';
import { getFirstNameScheme } from '@Components/inputs/FirstNameInput';
import { getLastNameScheme } from '@Components/inputs/LastNameInput';
import { birthDateSchema } from '@Schema/birthDateSchema';
import { passportScheme } from '@Components/inputs/PassportInput';
import { AddressStateSchema } from '@Components/inputs/AddressStateDropDown';
import { getNationalIdSchemeByCountry } from '@Components/inputs/NationalIdInput';
import type { RegionCode } from 'google-libphonenumber';
import type { Country } from '@Types/country';

export interface UBOFormFields {
  [OnboardingFormFields.FIRST_NAME]: string;
  [OnboardingFormFields.LAST_NAME]: string;
  [OnboardingFormFields.EMAIL]: string;
  [OnboardingFormFields.OWNER_PERCENTAGE]: number;

  isUsResident: boolean;

  [OnboardingFormFields.SSN]?: string;

  [OnboardingFormFields.NATIONALITY]?: string;
  [OnboardingFormFields.PASSPORT_NUMBER]?: string;
  [OnboardingFormFields.NATIONAL_ID_NUMBER]?: string;
  [OnboardingFormFields.IDENTITY_NON_US_AGREEMENT_A]?: boolean;

  [OnboardingFormFields.DATE_OF_BIRTH]: string;
  [OnboardingFormFields.PHONE_NUMBER]: string;
  [OnboardingFormFields.PHONE_NUMBER_ISO_CODE]?: RegionCode | null;

  [OnboardingFormFields.LINE1]: string;
  [OnboardingFormFields.APARTMENT]?: string;
  [OnboardingFormFields.CITY]: string;
  [OnboardingFormFields.STATE]?: { value: string; title: string } | null;
  [OnboardingFormFields.COUNTRY]?: { code: string; name: string } | null;
  [OnboardingFormFields.ZIP]: string;
}

export const UBOFormSchema = (country: Country | undefined, nationality: Country | undefined) => {
  const sharedFields = {
    ...getFirstNameScheme().shape,
    ...getLastNameScheme().shape,
    [OnboardingFormFields.EMAIL]: emailSchema,
    [OnboardingFormFields.OWNER_PERCENTAGE]: z
      .number({
        error: t('enErrors:required'),
      })
      .min(25, t('enErrors:addOwner.ownershipPercentageTooLow'))
      .max(100),
    ...birthDateSchema.shape,
    [OnboardingFormFields.PHONE_NUMBER]: z
      .string()
      .trim()
      .refine((value) => isValidNum(value), {
        message: t('enErrors:phone'),
      }),
    ...Line1Schema.shape,
    ...ApartmentSchema.shape,
    ...citySchema.shape,
  };

  const usResidentSchema = z.object({
    ...sharedFields,
    isUsResident: z.literal(true),
    ...ssnSchema.shape,
    ...AddressStateSchema.shape,
    ...zipSchema.shape,
  });

  const nonUsResidentSchema = z.object({
    ...sharedFields,
    isUsResident: z.literal(false),
    [OnboardingFormFields.NATIONALITY]: z.string().min(1, t('enErrors:required')),
    ...passportScheme.shape,
    [OnboardingFormFields.COUNTRY]: z.object(
      { code: z.string(), name: z.string() },
      { error: t('enErrors:required') }
    ),
    ...getNationalIdSchemeByCountry(nationality).shape,
    [OnboardingFormFields.IDENTITY_NON_US_AGREEMENT_A]: z
      .boolean({ error: () => t('enErrors:required') })
      .refine((val) => val === true, { error: () => t('enErrors:required') }),
    ...getZipSchemeByCountry(country).shape,
  });

  return z.discriminatedUnion('isUsResident', [usResidentSchema, nonUsResidentSchema]);
};

export type UBOFormSchemaType = z.infer<ReturnType<typeof UBOFormSchema>>;
